import {
  emptyArray2Undefined,
  optional,
  tryCall,
  tryMapNew,
  tryNew,
  tryNewDate,
} from '@/helpers/dto2resource';
import DetalheDividaDto from '../dto/DetalheDividaDto';
import DividaDto from '../dto/DividaDto';
import DividaParametros from '../dto/DividaParametros';
import type CaracteristicasDividaDto from '../dto/CaracteristicasDividaDto';
import { NivelSincronizacao, StatusDivida } from '../enums';
import { FluxoDividaType } from '../FluxoDividaType';
import ComposicaoDividaResource from './ComposicaoDividaResource';
import OfertaResource from './OfertaResource';
import ParcelaResource from './ParcelaResource';
import RegrasDeNegociacaoResource from './RegrasDeNegociacaoResource';

export default class DividaResource {
  id: number;
  codigoContrato: string | null;
  descricao: string | null;
  status: StatusDivida;
  dataOrigem: Date;
  dataUltimaAtualizacao: Date;
  valorPrincipal?: number;
  dataLimitePagamento: Date | null;
  dataValidade?: Date;
  datasPrimeiroPagamento?: Date[];

  valorTotal?: number;
  valorAcrescimos?: number;
  valorVencido?: number;
  valorVincendo?: number;

  quantidadeIteracoesDisponiveis: number;
  regrasDeNegociacao: RegrasDeNegociacaoResource | null;
  parcelas: ParcelaResource[];
  composicao: ComposicaoDividaResource[];
  flags: string[];
  credor: string;
  api: string;
  produto?: string;
  loja: string;
  credorOrigem?: string;
  icone: string;
  tipoNegociacao: string;
  deprecatedLogo?: string;
  fluxoNegociacao?: string | null;
  detalhe: DetalheDividaDto | null;
  ofertas: OfertaResource[];
  interface: string;
  valorAtualizado: number;
  dataChegada?: Date;
  dataLimiteNegociacao?: Date;
  codigoCredor: number;
  parametros?: DividaParametros;
  nivelSincronizacao?: NivelSincronizacao;
  caracteristicas?: CaracteristicasDividaDto;

  readonly ofertasByDate: Record<number, OfertaResource['id'][]> = {};
  readonly ofertasById: Record<OfertaResource['id'], OfertaResource> = {};

  constructor(resource: DividaDto) {
    this.id = resource.id;
    this.codigoContrato = resource.codigoContrato;
    this.descricao = resource.descricao;
    this.status = resource.status;
    this.dataOrigem = new Date(resource.dataOrigem);
    this.dataUltimaAtualizacao = new Date(resource.dataUltimaAtualizacao);
    this.valorPrincipal = optional(resource.valorPrincipal);

    this.dataLimitePagamento = tryCall((d) => new Date(d), resource.dataLimitePagamento) || null;
    this.dataValidade = tryNewDate(resource.dataValidade);
    this.datasPrimeiroPagamento = emptyArray2Undefined(resource.datasPrimeiroPagamento)
      ?.map((d) => new Date(d));

    this.valorTotal = optional(resource.valorTotal);
    this.valorAcrescimos = optional(resource.valorAcrescimos);
    this.valorVencido = optional(resource.valorVencido);
    this.valorVincendo = optional(resource.valorVincendo);

    this.quantidadeIteracoesDisponiveis = resource.quantidadeIteracoesDisponiveis;
    this.regrasDeNegociacao = tryNew(RegrasDeNegociacaoResource, resource.regrasDeNegociacao)
      || null;
    this.parcelas = resource.parcelas?.map((e) => new ParcelaResource(e)) || [];
    this.composicao = resource.composicao?.map((e) => new ComposicaoDividaResource(e)) || [];
    this.flags = resource.flags;
    this.credor = resource.credor;
    this.api = resource.api;
    this.produto = resource.detalhe?.produto || resource.produto || undefined;
    this.loja = resource.loja;
    this.credorOrigem = optional(resource.credorOrigem);
    this.icone = resource.icone;
    this.tipoNegociacao = resource.tipoNegociacao;
    this.deprecatedLogo = resource.logo;
    this.detalhe = resource.detalhe;

    this.ofertas = tryMapNew(OfertaResource, resource.ofertas) || [];
    this.ofertas.forEach((oferta) => {
      this.ofertasById[oferta.id] = oferta;
    });

    this.fluxoNegociacao = resource.fluxoNegociacao;
    this.interface = (resource.detalhe?.tema || resource.api).replace(/^mc-/, '');
    this.valorAtualizado = resource.valorAtualizado;
    this.dataChegada = tryNewDate(resource.dataChegada);
    this.dataLimiteNegociacao = tryCall((d) => new Date(d), resource.dataLimiteNegociacao);
    this.codigoCredor = resource.codigoCredor;
    this.parametros = optional(resource.parametros);
    this.nivelSincronizacao = optional(resource.nivelSincronizacao);
    this.caracteristicas = optional(resource.caracteristicas);
  }

  get logo(): string {
    return this.detalhe?.logo || this.deprecatedLogo || this.api;
  }

  get possuiJuros(): boolean {
    return (this.valorAcrescimos ?? 0) > 0;
  }

  get melhorOferta(): OfertaResource | undefined {
    return this.ofertas.find((e) => e.prioridade === 0);
  }

  get podeSerNegociada(): boolean {
    return this.status === StatusDivida.AguardandoNegociacao
      || this.status === StatusDivida.EmNegociacao;
  }

  get possuiComposicao(): boolean {
    return this.composicao.length > 1;
  }

  get possuiOferta(): boolean {
    return this.ofertas.length > 0;
  }

  get deveExibirNaListagemNegociavel(): boolean {
    return this.podeSerNegociada && (this.possuiOferta || this.ofuscada);
  }

  get fluxo(): FluxoDividaType {
    if (this.fluxoNegociacao === 'PreCreli') {
      return 'PreCreli';
    }

    return 'Oferta';
  }

  get contratos() {
    return this.codigoContrato
      ? [this.codigoContrato]
      : this.composicao
        .filter((comp) => comp.codigoContrato)
        .map((comp) => comp.codigoContrato);
  }

  get permitePagamentoAposVencimento() {
    return !this.parametros || this.parametros.permitePagamentoAposVencimento;
  }

  get ofuscada(): boolean {
    return !!this.parametros?.requerAutenticacao2FA;
  }

  get meioPagamentoDiferenteColchao(): boolean {
    return !!this.parametros?.meioPagamentoDiferenteColchao;
  }

  get restringirMeioPagamentoFormalizacao(): boolean {
    return !!this.parametros?.restringirMeioPagamentoFormalizacao;
  }

  get possuiSincronizacaoParcela(): boolean {
    if (this.parametros?.possuiSincronizacaoParcela === undefined) {
      return true;
    }
    return !!this.parametros?.possuiSincronizacaoParcela;
  }

  get sincronizacaoIncompleta(): boolean {
    return (
      this.nivelSincronizacao !== undefined
      && this.nivelSincronizacao !== NivelSincronizacao.Completo
    );
  }

  getOfertasByDate(date: Date): OfertaResource[] | undefined {
    return this.ofertasByDate[date.getTime()]
      ?.map((id) => this.ofertasById[id]);
  }

  setOfertasOfDate(date: Date, ofertas: OfertaResource[]) {
    this.ofertasByDate[date.getTime()] = ofertas.map((o) => o.id);
    ofertas.forEach((oferta) => {
      this.ofertasById[oferta.id] = oferta;
    });
  }

  updateOfertaComDetalhe(novaOferta: OfertaResource) {
    const ofertaId = novaOferta.id;
    if (this.ofertas) {
      const index = this.ofertas.findIndex((o) => o.id === ofertaId) ?? -1;
      if (index >= 0) this.ofertas[index] = novaOferta;
    }

    this.ofertasById[ofertaId] = novaOferta;
  }
}
